import { FC, useMemo } from 'react'
import styled from 'styled-components'

import {
  Clock,
  ResponsiveImage,
  Spacer,
  Trash,
  TypeStyleBodySmall,
} from '@syconium/little-miss-figgy'

import { NextLink } from '../../../../../app/_components/navigation/NextLink'
import { BadgesSection } from '../../../../../app/_components/pages/product-pages/ProductPage/ProductHighlights/BadgesSection/index'
import { useCartDialog } from '../../../../../app/_providers/CartProvider.client'
import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useSiteWideMessaging } from '../../../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useGoogleTagManager } from '../../../../../lib/hooks/useGoogleTagManager'
import { CartItem } from '../../../../../types/figs'
import { AddEmbroideryUpsellButton } from '../../CartEmbroideryUpsell/AddEmbroideryButton'
import { CartItemQuantity } from '../../CartItemQuantity/CartItemQuantity'
import { filterPropertiesToDisplay } from '../../utils'
import { CartItemAttributes } from '../CartItemAttributes/CartItemAttributes'
import { PriceAndDeleteButton } from '../PriceAndDeleteButton/PriceAndDeleteButton'
import { DeleteIconButton, DeleteIconColumn } from '../PriceAndDeleteButton/styles'

import {
  Body,
  Bottom,
  Details,
  DetailsColumn,
  DetailsRow,
  FitDescription,
  ImagePlaceholder,
  ImageWrapper,
  OptionsText,
  SiteWidePromoWrap,
  Title,
  Top,
} from './styles'

const hasEnumerableGiftCardProperties = (item: CartItem): boolean => {
  return Boolean(
    item.productType === 'Gift Card' &&
      item.properties.name &&
      item.properties.email &&
      item.properties.message
  )
}

const getDisplayableProperties = (item: CartItem): CartItem['properties'] => {
  if (!hasEnumerableGiftCardProperties(item)) return item.properties
  return {
    Name: item.properties.name,
    Email: item.properties.email,
    Message: item.properties.message,
  }
}

interface RegularCartItemProps {
  cartIsUpdating: boolean
  cartItem: CartItem
  decrementQuantity(): void
  eventCategory: 'minicart' | 'cart'
  incrementQuantity(): void
  removeItem(): void
  setQuantity(newQuantity: number): void
  isBundleGroup?: boolean
  uniqueId: string
}

export const RegularCartItem: FC<RegularCartItemProps> = ({
  cartIsUpdating,
  cartItem,
  decrementQuantity,
  eventCategory,
  incrementQuantity,
  removeItem,
  setQuantity,
  uniqueId,
  isBundleGroup,
}) => {
  const {
    isLowInventory,
    colorDisplayValue,
    effectivePrice,
    shopifyProductId,
    finalSale,
    fitDisplayValue,
    fullPrice,
    image,
    productGroupHandle,
    productType,
    properties,
    quantity,
    sizeDisplayValue,
    sku,
    swPromoEligible,
    title,
    variantId,
    urlPath,
  } = cartItem
  const { region } = useLocalization()
  const { siteWidePromotionMessage: siteWidePromo } = useSiteWideMessaging()
  const {
    cart: miniCartCopy,
    pages: {
      product: { lowInventoryWarning },
    },
  } = useTranslation()
  const removeItemAriaLabel = miniCartCopy.removeItemLabel.replace('{{itemTitle}}', title)

  const { close: closeCartDialog } = useCartDialog()
  const { pushProductClick } = useGoogleTagManager()
  const propertiesToDisplay = filterPropertiesToDisplay(getDisplayableProperties(cartItem))

  const linkHref = urlPath

  const { [miniCartCopy.finalSale]: finalSaleProp, ...otherPropertiesToDisplay } =
    propertiesToDisplay

  const isFinalSaleVisible = finalSale || finalSaleProp

  // Get the promotional text to display in the details section.
  // Returns "undefined" if no promotional text should be rendered.
  const promoText = useMemo(() => {
    if (properties['_isPortalColor'] === 'true' || properties['_inTeamStore'] === 'true')
      return undefined
    // If the product is site-wide promo eligible, use the site-wide promo text
    if (swPromoEligible && siteWidePromo) return siteWidePromo
    return undefined
  }, [properties, swPromoEligible, siteWidePromo])

  return (
    <Body
      data-testid='cart-item'
      data-cart-item-id={shopifyProductId}
      $isBundleGroup={isBundleGroup}
    >
      <Top>
        <NextLink
          href={linkHref}
          onClick={() => {
            pushProductClick({
              variant: {
                category: productType,
                currencyType: region.currency,
                externalParentId: shopifyProductId,
                discountPrice: effectivePrice,
                handle: productGroupHandle,
                price: fullPrice,
                shopifyId: variantId,
                sku,
              },
              list: `${eventCategory}-cart`,
            })
            closeCartDialog()
          }}
        >
          <ImageWrapper>
            {image.url ? (
              <ResponsiveImage
                alt={title}
                aspectRatios={{
                  sm: 1,
                  md: 1,
                }}
                src={image.url}
                loading='lazy'
                widths={{
                  unit: 'px',
                  sm: 100,
                  md: 100,
                }}
              />
            ) : (
              <ImagePlaceholder />
            )}
          </ImageWrapper>
        </NextLink>

        <Details id={`Details-${uniqueId}`}>
          <DetailsRow>
            <Title asTag='h3' data-minicart-item={title}>
              {title}
            </Title>
            <DeleteIconColumn>
              <DeleteIconButton onClick={removeItem} aria-label={removeItemAriaLabel}>
                <Trash stroke='currentColor' />
              </DeleteIconButton>
            </DeleteIconColumn>
          </DetailsRow>
          <Spacer height={4} />
          <DetailsRow $hasOffsetBelow>
            <OptionsText>
              {sizeDisplayValue}
              {productType !== 'Gift Card' && (
                <>
                  {fitDisplayValue && <FitDescription>• {fitDisplayValue}</FitDescription>}
                  &nbsp;• {colorDisplayValue}
                </>
              )}
            </OptionsText>
          </DetailsRow>
          {isFinalSaleVisible && <BadgesSection badges={[miniCartCopy.finalSale]} />}
          {!!promoText && <SiteWidePromoWrap>{promoText}</SiteWidePromoWrap>}
          <AddEmbroideryUpsellButton cartItem={cartItem} />
          <Spacer height={16} />
          <DetailsRow>
            <CartItemQuantity
              cartIsUpdating={cartIsUpdating}
              decrementQuantity={decrementQuantity}
              incrementQuantity={incrementQuantity}
              setQuantity={setQuantity}
              quantity={quantity}
            />
            <DetailsColumn>
              <PriceAndDeleteButton effectivePrice={effectivePrice} fullPrice={fullPrice} />
            </DetailsColumn>
          </DetailsRow>
        </Details>
      </Top>
      <Bottom />
      <CartItemAttributes attributes={otherPropertiesToDisplay} />
      {isLowInventory && (
        <LowInventoryTypography>
          <Clock />
          {lowInventoryWarning}
        </LowInventoryTypography>
      )}
    </Body>
  )
}

const LowInventoryTypography = styled(TypeStyleBodySmall)`
  color: ${o => o.theme.color.text.caution.on.background};
  display: flex;
  align-items: center;
  gap: ${o => o.theme.spacing(2)};
  margin: ${o => o.theme.spacing(6, 0, 0, 0)};
`
